import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import type { SubmittedJob } from '@/features/jobs/details/SubmittedJob'
import type { RequestedSupportingItem } from '@/features/jobs/details/tax-type/info-gathering/RequestedSupportingItem'
import type { DocumentSupportingItemInput } from '@/features/jobs/details/tax-type/info-gathering/documents/DocumentSupportingItemInput'
import type { InfoGatheringStore } from '@/features/jobs/details/tax-type/info-gathering/InfoGatheringStore'
import type {
  DocumentLinkingViewModelFactory,
  DocumentsListViewModelFactory,
} from '@/features/docs'
import { task } from 'mobx-task'
import { tapAction, toStream } from '@/utils/mobx-util'
import { concatMap, switchMap } from 'rxjs'
import { withAbortSignal } from '@/utils/rx-util'
import { action, computed, observable, runInAction } from 'mobx'
import { ModalViewModel } from '@/infra/ModalViewModel'
import type { Document } from '@/features/docs/Document'
import type { Id } from '@/infra/id'
import type { SnackbarRetryWrapperFactory } from '@/features/shell'

/**
 * View model for a document supporting item.
 */
export class DocumentRequestedSupportingItemViewModel
  implements ActivationLifecycle
{
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * Tracks the provided document IDs.
   * Syncs with the source item's provided documents list
   * Will be optimistically updated when providing new documents.
   * This prevents the document list filter from causing a flicker when
   * an uploading document transitions to uploaded but hasn't yet
   * been added to the item's `provided` list.
   * @private
   */
  private readonly _providedDocumentIds = observable.set<Id>()

  /**
   * The supporting item input type.
   */
  readonly type = 'Document' as const

  /**
   * View model for when showing the item in a dialog.
   */
  readonly dialog = new ModalViewModel()

  /**
   * View model for documents.
   */
  readonly documentsListVm

  /**
   * View model for linking documents.
   */
  readonly linkDocumentsVm

  constructor(
    public readonly job: SubmittedJob,
    public readonly item: RequestedSupportingItem<DocumentSupportingItemInput>,
    private readonly _infoGatheringStore: InfoGatheringStore,
    private readonly _createSnackbarRetryWrapper: SnackbarRetryWrapperFactory,
    createDocumentsListVm: DocumentsListViewModelFactory,
    createLinkDocumentsVm: DocumentLinkingViewModelFactory,
  ) {
    this.upload = this.upload.bind(this)

    this.documentsListVm = createDocumentsListVm({
      fetchAuthor: true,
      filter: (doc) => this._providedDocumentIds.has(doc.id),
      onRemove: (doc) => this._removeProvidedDocument(doc),
      context: {
        type: 'Job',
        jobId: this.job.id,
      },
    })

    this.linkDocumentsVm = createLinkDocumentsVm({
      filter: (doc) =>
        !doc.jobIds.includes(this.job.id) &&
        doc.typeId === this.item.input.documentTypeShortId,
      onLinkingCompleted: (doc) => this._provideDocument(doc),
      context: {
        type: 'Job',
        jobId: this.job.id,
      },
    })

    this._lifecycle.manage(this.documentsListVm)
    this._lifecycle.manage(this.linkDocumentsVm)

    // Make sure we're fetching any documents that appear in the document IDs
    // of the source item (e.g. realtime updates).
    this._lifecycle.manage(
      toStream(() => this.item.input.documentIds).pipe(
        switchMap((ids) =>
          withAbortSignal((signal) =>
            this.documentsListVm.fetchByIds(ids, signal).then(() => ids),
          ),
        ),
        tapAction((newIds) => {
          // Remove documents that aren't part of the new ID list.
          const docsToRemove = this.documentsListVm.allDocuments
            .filter((d) => !newIds.includes(d.doc.id))
            .map((d) => d.doc)
          docsToRemove.forEach(this.documentsListVm.removeDocument)
        }),
      ),
    )

    // Provide the document once one has been uploaded.
    this._lifecycle.manage(
      this.documentsListVm.uploaderVm.uploaded$.pipe(
        concatMap((doc) => this._provideDocument(doc)),
      ),
    )

    // Sync the provided docs set with that of the item.
    this._lifecycle.manage(
      toStream(() => this.item.input.documentIds, true).pipe(
        tapAction((ids) => this._providedDocumentIds.replace(ids)),
      ),
    )
  }

  /**
   * Gets whether the item is completed.
   */
  @computed get isCompleted() {
    return this.item.isCompleted
  }

  /**
   * How many documents have been provided.
   */
  @computed get documentCount() {
    return this.item.input.documentIds.length
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
    void this.fetch(this._lifecycle.signal)
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Shows the dialog for this item.
   */
  @action.bound
  showDialog() {
    this.dialog.show()
  }

  /**
   * Fetches the documents for this item.
   */
  fetch = task(async (signal: AbortSignal) => {
    const withRetry = this._createSnackbarRetryWrapper(
      'Unable to retrieve document information',
      signal,
    )
    await withRetry(() =>
      this.documentsListVm.fetchByIds(this.item.input.documentIds, signal),
    )
  })

  /**
   * Uploads the specified documents.
   *
   * @param files
   */
  async upload(files: File[]) {
    await this.documentsListVm.uploaderVm.upload(
      files,
      this.item.input.documentTypeShortId,
    )
  }

  /**
   * Provides the document.
   */
  private async _provideDocument(document: Document) {
    // Optimistically update the internal set so the filter callback on
    // the list VM doesn't filter out the document after it's been uploaded
    // but before it's been provided.
    runInAction(() => {
      this._providedDocumentIds.add(document.id)
    })
    await this._infoGatheringStore
      .provideDocument(this.job.id, this.item, document.id)
      .catch((err) => {
        // Undo in case of an error.
        runInAction(() => {
          this._providedDocumentIds.delete(document.id)
        })
        throw err
      })

    this.documentsListVm.addDocument(document)
  }

  /**
   * Removes the provided document.
   *
   * @param doc
   */
  private async _removeProvidedDocument(doc: Document) {
    // Remove from the checklist.
    await this._infoGatheringStore.removeDocument(
      this.job.id,
      this.item,
      doc.id,
    )

    runInAction(() => {
      this._providedDocumentIds.delete(doc.id)
      this.documentsListVm.removeDocument(doc)
    })
  }
}
